import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { ButtonColor } from "@/components/Button/types";
import { Icon, Modal } from "@/components";
import { useSnackBar } from "@/utils/useSnackbar";
import { classNames } from "@/utils/classNames";

import {
	useDisconnectDynamicsOAuthMutation,
	useGetDynamicsOAuthStatusQuery,
} from "@/pages/Private/redux/dynamics/dynamics.api";

import { DynamicsConnectionTab } from "./DynamicsConnectionTab";
import { DynamicsDealConfigTab } from "./DynamicsDealConfigTab";
import { DynamicsFieldMappingModal } from "./DynamicsFieldMappingModal";
import { DynamicsLeadConfigTab } from "./DynamicsLeadConfigTab";
import { DynamicsSyncLogsTab } from "./DynamicsSyncLogsTab";
import { DynamicsSyncSettingsTab } from "./DynamicsSyncSettingsTab";
import { DynamicsUserMappingTab } from "./DynamicsUserMappingTab";

enum DynamicsTabs {
	CONNECTION = "CONNECTION",
	SETTINGS = "SETTINGS",
	DEAL_CONFIGURATION = "DEAL_CONFIGURATION",
	LEAD_CONFIGURATION = "LEAD_CONFIGURATION",
	USER_MAPPING = "USER_MAPPING",
	SYNC_LOGS = "SYNC_LOGS",
}

export const DynamicsSettings: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`company.msdynamics.${key}`), [t]);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [searchParams, setSearchParams] = useSearchParams();
	const currentTab = (searchParams.get("msdynamicsTab") as DynamicsTabs) || DynamicsTabs.CONNECTION;

	const [isFieldMappingOpen, setIsFieldMappingOpen] = useState(false);
	const [showDisconnectModal, setShowDisconnectModal] = useState(false);

	const { data: oauthStatus, refetch: refetchOAuthStatus } = useGetDynamicsOAuthStatusQuery();
	const [disconnectOAuth, { isLoading: isDisconnecting }] = useDisconnectDynamicsOAuthMutation();

	const isConnected = oauthStatus?.connected ?? false;

	const handleTabChange = useCallback(
		(tab: DynamicsTabs) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("msdynamicsTab", tab);

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const handleDisconnectOAuth = async () => {
		try {
			await disconnectOAuth().unwrap();
			setShowDisconnectModal(false);
		} catch (error) {
			const status = (error as { status?: number })?.status;

			if (status === 400) {
				setShowDisconnectModal(false);
				handleOpenSnackbar(ts("oauth.alreadyDisconnected"));
				refetchOAuthStatus();

				return;
			}

			handleOpenSnackbar(ts("oauth.disconnectError"));
		}
	};

	const tabItems = [
		{ key: DynamicsTabs.CONNECTION, label: ts("tabs.connection") },
		{ key: DynamicsTabs.SETTINGS, label: ts("tabs.settings") },
		{ key: DynamicsTabs.DEAL_CONFIGURATION, label: ts("tabs.dealConfiguration") },
		{ key: DynamicsTabs.LEAD_CONFIGURATION, label: ts("tabs.leadConfiguration") },
		{ key: DynamicsTabs.USER_MAPPING, label: ts("tabs.userMapping") },
		{ key: DynamicsTabs.SYNC_LOGS, label: ts("tabs.syncLogs") },
	];

	const connectionContent = (
		<DynamicsConnectionTab
			isDisconnecting={isDisconnecting}
			oauthStatus={oauthStatus}
			ts={ts}
			onOpenFieldMapping={() => setIsFieldMappingOpen(true)}
			onRequestDisconnect={() => setShowDisconnectModal(true)}
		/>
	);

	return (
		<>
			<div className="p-4 rounded-xl border border-border-light bg-brand-white">
				<div className="flex items-center justify-between mb-4">
					<div className="text-smd">{ts("title")}</div>
					{isConnected ? (
						<div className="flex items-center gap-1 text-sm text-status-success">
							<Icon className="w-4 h-4" icon={faCheck} />
							{ts("connected")}
						</div>
					) : (
						<div className="flex items-center gap-1 text-sm text-text-meta">
							<Icon className="w-4 h-4" icon={faXmark} />
							{ts("notConnected")}
						</div>
					)}
				</div>

				{isConnected ? (
					<>
						<div className="flex border-b border-border-light mb-4">
							{tabItems.map((tab) => (
								<button
									key={tab.key}
									className={classNames(
										"px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer",
										currentTab === tab.key
											? "border-brand-light text-brand-light"
											: "border-transparent text-text-meta hover:text-text hover:border-border-light"
									)}
									type="button"
									onClick={() => handleTabChange(tab.key)}
								>
									{tab.label}
								</button>
							))}
						</div>

						{currentTab === DynamicsTabs.CONNECTION && connectionContent}
						{currentTab === DynamicsTabs.SETTINGS && (
							<DynamicsSyncSettingsTab isConnected={isConnected} ts={ts} />
						)}
						{currentTab === DynamicsTabs.DEAL_CONFIGURATION && <DynamicsDealConfigTab />}
						{currentTab === DynamicsTabs.LEAD_CONFIGURATION && <DynamicsLeadConfigTab />}
						{currentTab === DynamicsTabs.USER_MAPPING && <DynamicsUserMappingTab />}
						{currentTab === DynamicsTabs.SYNC_LOGS && <DynamicsSyncLogsTab />}
					</>
				) : (
					connectionContent
				)}
			</div>

			{isFieldMappingOpen && (
				<DynamicsFieldMappingModal
					isOpen={isFieldMappingOpen}
					onClose={() => setIsFieldMappingOpen(false)}
				/>
			)}

			{showDisconnectModal && (
				<Modal
					handleClose={() => setShowDisconnectModal(false)}
					handleSave={handleDisconnectOAuth}
					isLoading={isDisconnecting}
					isOpened={showDisconnectModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={ts("disconnect")}
					title={ts("oauth.disconnectTitle")}
				>
					<div className="text-sm">{ts("oauth.disconnectBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
